/**
 * Sign in with Apple and Google, through the platforms' own sheets.
 *
 * The app never sees a password and never holds an OAuth secret: the native
 * sheet returns a signed identity token, the server verifies it against the
 * provider's published keys, and only then is a session issued. Nothing the
 * client claims about itself is trusted.
 *
 * Each attempt carries a fresh random nonce, and the raw value always goes to
 * the server. What goes to the provider differs:
 * - Apple copies whatever nonce the request carries into its token unchanged,
 *   so the app hands Apple the lowercase hex SHA-256 of the raw nonce and the
 *   server checks token.nonce === sha256(raw). Neither the plugin nor Apple
 *   hashes it for us, on iOS, Android or the web.
 * - Google puts the raw nonce in its token, so it is passed as-is and the
 *   server compares it directly.
 * The nonce is single-use server-side, so a captured token cannot be replayed.
 *
 * Both providers come from one plugin (@capgo/capacitor-social-login) so there
 * is a single native integration to configure and keep current.
 */

export type Provider = 'apple' | 'google';

export interface SocialCredential {
    provider: Provider;
    /** The raw nonce. The server hashes it itself for Apple. */
    nonce: string;
    /** Apple's identity token. */
    identityToken?: string;
    /** Google's ID token. */
    idToken?: string;
    /** Apple only, and only on first authorisation: exchanged server-side for the refresh token used to revoke on account deletion. */
    authorizationCode?: string;
    /** Apple only, and only on first authorisation. */
    fullName?: { givenName?: string | null; familyName?: string | null } | null;
}

/**
 * Native configuration, supplied by the server. The web client ID and Apple's
 * settings can change without a new build; the Google iOS client cannot (see
 * builtGoogleIosClientId).
 */
export interface SocialConfig {
    googleWebClientId?: string | null;
    googleIosClientId?: string | null;
    appleClientId?: string | null;
    appleRedirectUrl?: string | null;
}

/** Which providers the server says it can verify. */
export interface ServerProviders {
    apple?: boolean;
    google?: boolean;
}

/**
 * The Google iOS client this build was made for, baked in by `npm run build:ios`
 * from the URL scheme in ios/App/App/Config/Google.xcconfig (see
 * scripts/ios-google-client.js). GoogleSignIn crashes the app, uncatchably, when
 * it is handed a client whose reversed ID is not one of the app's URL schemes,
 * so on iOS Google is used only with exactly this client. A build with the
 * placeholder scheme has none, and never offers Google.
 */
export function builtGoogleIosClientId(): string | null {
    return (process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID || '').trim() || null;
}

/** True when the server's Google iOS client is the one this build can open. */
function googleIosClientMatches(config?: SocialConfig | null): boolean {
    const built = builtGoogleIosClientId();
    return Boolean(built) && config?.googleIosClientId === built;
}

function randomNonce(): string {
    const bytes = new Uint8Array(24);
    globalThis.crypto.getRandomValues(bytes);
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

/** Lowercase hex SHA-256, the form the server compares Apple's token nonce against. */
async function sha256Hex(value: string): Promise<string> {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) throw new Error('Secure sign-in is not available on this device');
    const digest = new Uint8Array(await subtle.digest('SHA-256', new TextEncoder().encode(value)));
    let hex = '';
    for (let index = 0; index < digest.length; index += 1) hex += digest[index].toString(16).padStart(2, '0');
    return hex;
}

function capacitor(): any {
    return (globalThis as any).Capacitor;
}

export function isNative(): boolean {
    return Boolean(capacitor()?.isNativePlatform?.());
}

export function platform(): 'ios' | 'android' | 'web' {
    const value = capacitor()?.getPlatform?.();
    return value === 'ios' || value === 'android' ? value : 'web';
}

function socialLogin(): any {
    // Resolved lazily: on the web build the plugin is absent and the social
    // buttons are simply not offered.
    return capacitor()?.Plugins?.SocialLogin ?? null;
}

let initialised = false;

/**
 * The options handed to the plugin's initialize().
 *
 * iOS uses the native Sign in with Apple sheet, which identifies the app by its
 * bundle ID: no Services ID, and no redirect URL (a redirect URL sends the
 * plugin down a server POST flow our API does not implement). Google on iOS
 * needs the iOS client ID, and only the one this build's URL scheme belongs to
 * (builtGoogleIosClientId); any other would crash the app at sign-in.
 * Android and the web drive Apple through the Services ID and its redirect.
 */
export function pluginOptions(config: SocialConfig, providers?: ServerProviders | null): Record<string, unknown> {
    const options: Record<string, unknown> = {};
    const ios = platform() === 'ios';
    // An older server may not report providers: fall back to "configured" meaning "on".
    const appleOn = providers ? Boolean(providers.apple) : Boolean(config.appleClientId);
    const googleOn = providers ? Boolean(providers.google) : Boolean(config.googleWebClientId);

    if (googleOn && config.googleWebClientId && (!ios || googleIosClientMatches(config))) {
        options.google = { webClientId: config.googleWebClientId, iOSClientId: config.googleIosClientId ?? undefined };
    }
    if (appleOn) {
        if (ios) options.apple = {};
        else if (config.appleClientId) options.apple = { clientId: config.appleClientId, redirectUrl: config.appleRedirectUrl ?? undefined };
    }
    return options;
}

/** Configure the native plugin once per app launch. */
export async function initialiseSocialLogin(config: SocialConfig, providers?: ServerProviders | null): Promise<boolean> {
    const plugin = socialLogin();
    if (!plugin || initialised) return initialised;
    const options = pluginOptions(config, providers);
    if (Object.keys(options).length === 0) return false;
    try {
        await plugin.initialize(options);
        initialised = true;
    } catch {
        initialised = false;
    }
    return initialised;
}

/**
 * Which buttons to offer.
 *
 * Apple requires its own button wherever another third-party sign-in appears in
 * an iOS app, so on iOS Apple is listed first and Google is offered only
 * alongside it, and only when the server's iOS Google client is the one this
 * build was made for.
 */
export function availableProviders(serverProviders?: ServerProviders | null, native?: SocialConfig | null): Provider[] {
    // No native plugin means no sheet to open. The web build offers the password
    // form only; a button that can only fail is worse than no button.
    if (!socialLogin()) return [];
    const list: Provider[] = [];
    // An older or partially-configured server may not report providers at all;
    // offering a button that cannot work is worse than offering none.
    if (serverProviders?.apple) list.push('apple');
    if (serverProviders?.google) list.push('google');
    if (platform() === 'ios') {
        const googleUsable = list.includes('apple') && googleIosClientMatches(native);
        return googleUsable ? list : list.filter(provider => provider !== 'google');
    }
    return list.sort((a, b) => (a === 'google' ? -1 : b === 'google' ? 1 : 0));
}

/** Apple's iOS SDK only knows the raw scope values 'email' and 'full_name'; the web and Android flows use OAuth's 'name'. */
function appleScopes(): string[] {
    return platform() === 'ios' ? ['email', 'full_name'] : ['email', 'name'];
}

export async function signInWith(provider: Provider): Promise<SocialCredential> {
    const plugin = socialLogin();
    if (!plugin) throw new Error('This sign-in method is not available on this device');

    const nonce = randomNonce();
    const response = await plugin.login({
        provider,
        options: provider === 'apple'
            ? { scopes: appleScopes(), nonce: await sha256Hex(nonce) }
            // forcePrompt skips the plugin's "restore previous sign-in" shortcut,
            // which would hand back a cached token minted without this nonce.
            : { scopes: ['email', 'profile'], nonce, forcePrompt: true },
    });

    const result = response?.result ?? response ?? {};
    if (provider === 'apple') {
        const profile = result.profile ?? {};
        return {
            provider,
            nonce,
            identityToken: result.idToken ?? undefined,
            // Present only on the first authorisation; the server stores the
            // exchanged refresh token so deletion can revoke it.
            authorizationCode: result.authorizationCode ?? result.accessToken?.token ?? undefined,
            fullName: (profile.givenName || profile.familyName)
                ? { givenName: profile.givenName ?? null, familyName: profile.familyName ?? null }
                : null,
        };
    }
    return { provider, nonce, idToken: result.idToken ?? undefined };
}

/**
 * True when the player closed the Apple or Google sheet themselves: a normal
 * outcome, never an error to show. The plugin marks it with the code
 * USER_CANCELLED; its message is the system's own text, which for Apple is the
 * unhelpful "The operation couldn't be completed.
 * (com.apple.AuthenticationServices.AuthorizationError error 1001.)".
 */
const CANCELLED = /AuthorizationError error 1001\b|cancel/i;

export function isSignInCancelled(error: unknown): boolean {
    if (typeof error === 'string') return CANCELLED.test(error);
    if (!error || typeof error !== 'object') return false;
    if ((error as { code?: unknown }).code === 'USER_CANCELLED') return true;
    return CANCELLED.test(String((error as { message?: unknown }).message ?? ''));
}

/**
 * Forget the provider sessions the plugin keeps on the device, so the next
 * person to use this phone is not handed the previous player's Google account.
 * Best effort: a provider that was never opened on this device has nothing to
 * clear and may say so with an error, which is not worth surfacing.
 */
export async function signOutProviders(providers: ReadonlyArray<string> | null | undefined): Promise<void> {
    const plugin = socialLogin();
    if (!plugin?.logout || !providers) return;
    const linked = providers.filter((value): value is Provider => value === 'apple' || value === 'google');
    for (const provider of linked) {
        try {
            await plugin.logout({ provider });
        } catch {
            // Ignored on purpose; see above.
        }
    }
}

export const __testing = {
    randomNonce,
    sha256Hex,
    reset: () => { initialised = false; },
};
